import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Globe } from "lucide-react";
import { cn } from "@/lib/cn";
import { SUPPORTED_LANGS, LANG_LABELS, type SupportedLang } from "@/i18n";

// =============================================================================
// LanguageSwitcher — dropdown de sélection de langue (fr / en / es)
// Bouton compact (globe + code langue) qui ouvre un menu stylé,
// cohérent avec le UserMenu du header.
// =============================================================================

interface LanguageSwitcherProps {
  className?: string;
}

export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const choicesId = useId();

  const current = (
    SUPPORTED_LANGS.includes(i18n.resolvedLanguage as SupportedLang) ? i18n.resolvedLanguage : "fr"
  ) as SupportedLang;

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const select = (lang: SupportedLang) => {
    void i18n.changeLanguage(lang);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div
      ref={ref}
      className={cn("relative", className)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (!open) return;
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          triggerRef.current?.focus();
        }
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          const choices = [
            ...(ref.current?.querySelectorAll<HTMLButtonElement>("ul button") ?? []),
          ];
          const index = choices.indexOf(document.activeElement as HTMLButtonElement);
          const next =
            index < 0
              ? event.key === "ArrowDown"
                ? 0
                : choices.length - 1
              : (index + (event.key === "ArrowDown" ? 1 : -1) + choices.length) % choices.length;
          event.preventDefault();
          choices[next]?.focus();
        }
      }}
    >
      <button
        type="button"
        ref={triggerRef}
        lang="fr"
        onClick={() => setOpen((v) => !v)}
        className="focus-ring inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-ink-muted transition-colors hover:bg-bg-subtle hover:text-ink"
        aria-controls={open ? choicesId : undefined}
        aria-expanded={open}
        aria-label={`${current.toUpperCase()} : choisir la langue`}
      >
        <Globe className="size-4" />
        <span className="uppercase">{current}</span>
        <ChevronDown
          className={cn("size-3.5 text-ink-subtle transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <ul
          id={choicesId}
          lang="fr"
          aria-label="Langues disponibles"
          className="absolute right-0 top-10 z-30 w-40 origin-top-right animate-fade-in rounded-xl border border-line bg-surface p-1.5 shadow-elevated"
        >
          {SUPPORTED_LANGS.map((lang) => {
            const active = lang === current;
            return (
              <li key={lang}>
                <button
                  type="button"
                  aria-pressed={active}
                  lang={lang}
                  onClick={() => select(lang)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                    active
                      ? "bg-brand-muted/50 font-medium text-brand"
                      : "text-ink hover:bg-bg-subtle",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase text-ink-subtle">
                      {lang}
                    </span>
                    {LANG_LABELS[lang]}
                  </span>
                  {active && <Check className="size-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
